<template>
  <v-container class="risk-chart" fluid fill-height>
    <v-row
      v-if="!showChart"
      align="center"
      justify="center"
      style="height: calc(100% - 50px)"
      no-gutters
      class="empty-chart"
    >
      <v-col cols="12" class="title text-center">
        <v-icon large>fa-regular fa-function</v-icon>
        {{ reasonNoChart }}
      </v-col>
    </v-row>
    <v-row v-else align="center" justify="center" no-gutters>
      <v-col cols="12" style="height: 100%">
        <v-chart ref="chart" :theme="theme" autoresize manual-update :option="chartOptions"></v-chart>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  MarkLineComponent,
  DataZoomComponent,
  VisualMapComponent,
  VisualMapPiecewiseComponent
} from 'echarts/components'
import VChart from 'vue-echarts'
import { DateTime, Duration } from 'luxon'
import { ChartTheme } from '@itus/ui.common'
import { Weibull } from './distribution'

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  MarkLineComponent,
  DataZoomComponent,
  VisualMapComponent,
  VisualMapPiecewiseComponent
])

export default {
  name: 'RiskChart',
  components: {
    VChart
  },
  props: {
    distribution: {
      type: String,
      description: 'Distribution name',
      default: null
    },
    distributionProperties: {
      type: Object,
      description: 'Distribution properties',
      default: null
    },
    consequenceValue: {
      type: Number,
      description: 'Consequence in dollars to use in calculating risk',
      default: null
    },
    startDate: {
      type: String,
      description: 'Start date',
      default: null
    },
    missionTime: {
      type: String,
      description: 'Mission time',
      default: null
    },
    markLines: {
      type: Array,
      description: 'Optional array of mark line data that can override the default start date and mission date.',
      default: function () {
        return []
      }
    },
    timeZone: {
      type: String,
      description: 'Time zone to use for dates',
      default: 'local'
    }
  },
  data() {
    return {
      theme: ChartTheme.shine,
      chartOptions: {
        tooltip: {
          trigger: 'axis',
          formatter: this.tooltip,
          axisPointer: {
            type: 'line'
          }
        },
        grid: {
          left: 50,
          top: 50,
          right: 50,
          bottom: 50
        },
        xAxis: {
          name: 'Date',
          min(value) {
            return value.min
          },
          max(value) {
            return value.max + 1
          },
          axisLabel: {
            formatter: this.formatXAxis
          },
          minorTick: {
            show: true
          },
          splitLine: {
            lineStyle: {
              color: '#E0E0E0'
            }
          },
          minorSplitLine: {
            show: true,
            lineStyle: {
              color: '#F5F5F5'
            }
          }
        },
        yAxis: {
          name: 'Probability',
          axisLabel: {
            formatter(value, index) {
              return value * 100 + '%'
            }
          },
          minorTick: {
            show: true
          },
          splitLine: {
            lineStyle: {
              color: '#E0E0E0'
            }
          },
          minorSplitLine: {
            show: true,
            lineStyle: {
              color: '#F5F5F5'
            }
          }
        },
        dataZoom: [
          {
            show: true,
            disabled: true,
            type: 'inside',
            filterMode: 'none',
            xAxisIndex: [0],
            startValue: -5,
            endValue: 20
          },
          {
            show: true,
            disabled: true,
            type: 'inside',
            filterMode: 'none',
            yAxisIndex: [0],
            startValue: 0,
            endValue: 1
          }
        ],
        visualMap: {
          type: 'piecewise',
          show: false,
          dimension: 0,
          pieces: []
        },
        series: [
          {
            type: 'line',
            showSymbol: false,
            clip: false,
            areaStyle: {},
            lineStyle: {
              type: 'solid',
              color: this.$vuetify.theme.currentTheme.primary,
              width: 3
            },
            data: [],
            markLine: {
              symbol: 'none',
              lineStyle: {
                type: 'solid',
                width: 3
              },
              data: []
            }
          }
        ]
      }
    }
  },
  computed: {
    reasonNoChart() {
      let introMessage = 'Unable to generate chart. '
      if (!this.distribution) {
        return introMessage + 'No distribution function specified.'
      } else if (!this.startDate) {
        return introMessage + 'Start date not specified.'
      } else if (!this.missionTime) {
        return introMessage + 'Mission time not specified.'
      } else if (!this.distributionCalc || !this.distributionCalc.validate() || !this.consequenceValue) {
        return introMessage + 'Missing probability or consequence.'
      } else {
        return introMessage
      }
    },
    showChart() {
      return !(
        !this.distribution ||
        !this.startDate ||
        !this.missionTime ||
        !this.distributionCalc ||
        !this.distributionCalc.validate() ||
        !this.consequenceValue
      )
    },
    distributionCalc() {
      if (this.distribution === 'weibull') {
        return new Weibull(this.timeZone, this.startDate, this.missionTime, this.distributionProperties)
      } else if (this.distribution === 'none') {
        return new Weibull(this.timeZone, this.startDate, this.missionTime, {
          ...this.distributionProperties,
          shape: 1,
          type: 'cdf'
        })
      }
      return null
    },
    startDateObj() {
      return this.startDate ? DateTime.fromISO(this.startDate, { zone: this.timeZone }) : null
    },
    requiredMarkLines() {
      return this.markLines ? this.markLines.filter(x => x.alwaysShow) : []
    },
    optionalMarkLines() {
      return this.markLines ? this.markLines.filter(x => !x.alwaysShow) : []
    },
    riskCurveDuration() {
      let duration = this.missionTime
      if (this.startDate) {
        let maxMarkLineTime = null
        for (let markLine of this.requiredMarkLines) {
          if (
            maxMarkLineTime === null ||
            DateTime.fromISO(markLine.time, { zone: this.timeZone }) >
              DateTime.fromISO(maxMarkLineTime, { zone: this.timeZone })
          ) {
            maxMarkLineTime = markLine.time
          }
        }
        if (maxMarkLineTime) {
          const diff = DateTime.fromISO(maxMarkLineTime, { zone: this.timeZone }).diff(this.startDateObj, 'days')
          const days = Math.ceil(diff.days)
          const maxMarkLineDuration = Duration.fromObject({ days: days })
          if (maxMarkLineDuration > Duration.fromISO(this.missionTime)) {
            duration = maxMarkLineDuration.toISO()
          }
        }
      }
      return duration
    },
    seriesData() {
      if (!this.distributionCalc) return []

      return this.distributionCalc.calculate(this.riskCurveDuration)
    }
  },
  watch: {
    startDate: {
      deep: true,
      handler(newValue) {
        if (this.distributionCalc) {
          this.distributionCalc.startDate = newValue
        }
      }
    },
    missionTime: {
      deep: true,
      handler(newValue) {
        if (this.distributionCalc) {
          this.distributionCalc.missionTime = newValue
        }
      }
    },
    distributionProperties: {
      deep: true,
      handler(newValue) {
        if (!this.distributionCalc) {
          return
        }
        if (this.distribution === 'weibull') {
          this.distributionCalc.parameters = newValue
        } else if (this.distribution === 'none') {
          this.distributionCalc.parameters = { ...newValue, shape: 1, type: 'cdf' }
        }
      }
    },
    seriesData: {
      deep: true,
      immediate: true,
      handler(newValue) {
        let data = newValue || []
        this.processMarkLines(data)
        this.processZoomExtents(data)

        this.chartOptions.series[0].data = data

        if (!this.distributionCalc) {
          return
        }

        this.processShadingPieces()

        if (this.$refs.chart && this.showChart) {
          this.$refs.chart.setOption(this.chartOptions, true)
        }
      }
    }
  },
  methods: {
    formatXAxis(value, index) {
      return !this.startDate ? value : this.startDateObj.plus({ days: value }).toFormat('yyyy-MM-dd')
    },
    tooltip(params) {
      let probFormat = 'n/a'
      let riskFormat = 'n/a'
      if (params[0].data[1] !== null && params[0].data[1] !== undefined) {
        let prob = Math.trunc(params[0].data[1] * 100)
        probFormat = prob < 1 ? 'less than 1%' : prob > 99 ? 'greater than 99%' : prob + '%'
        let risk = Math.trunc((prob * this.consequenceValue) / 100)
        riskFormat = `$${risk.toLocaleString()}`
      }

      return `Probability: ${probFormat}<br/>Risk: ${riskFormat}<br/>Date: ${this.formatXAxis(params[0].data[0], 0)}`
    },
    getDay(date) {
      if (!date || !this.startDate) {
        return null
      }
      let dateObj = DateTime.fromISO(date, { zone: this.timeZone })
      let diff = dateObj.diff(this.startDateObj, 'days')
      return Math.ceil(diff.days)
    },
    processMarkLines(seriesData) {
      let lines = []
      let needsSort = false
      for (const markLine of this.requiredMarkLines) {
        const day = this.getDay(markLine.time)
        lines.push(this.toEChartMarkLine(markLine, day))
        if (!seriesData.some(x => x[0] === day)) {
          seriesData.push([day, null])
          needsSort = true
        }
      }

      if (needsSort) {
        seriesData.sort((a, b) => a[0] - b[0])
        needsSort = false
      }

      if (seriesData.length) {
        const min = seriesData[0][0]
        const max = seriesData[seriesData.length - 1][0]
        for (const markLine of this.optionalMarkLines) {
          const day = this.getDay(markLine.time)
          if (day <= max && day >= min) {
            lines.push(this.toEChartMarkLine(markLine, day))
            if (!seriesData.some(x => x[0] === day)) {
              seriesData.push([day, null])
              needsSort = true
            }
          }
        }
      }

      if (needsSort) {
        seriesData.sort((a, b) => a[0] - b[0])
      }

      this.chartOptions.series[0].markLine.data = lines
    },
    processZoomExtents(seriesData) {
      if (seriesData.length) {
        this.chartOptions.dataZoom[0].startValue = seriesData[0][0]
        this.chartOptions.dataZoom[0].endValue = seriesData[seriesData.length - 1][0]
      }
    },
    toEChartMarkLine(markLine, day) {
      return {
        name: markLine.name,
        xAxis: day,
        label: {
          formatter: '{b}',
          position: 'insideEndBottom'
        },
        itemStyle: {
          color: markLine.color
        },
        lineStyle: {
          type: markLine.lineStyleType || 'solid'
        }
      }
    },
    processShadingPieces() {
      //pieces for area shading
      let pieces = [
        {
          gte: 0,
          lt: this.chartOptions.dataZoom[0].endValue,
          color: 'rgba(15, 63, 114, 0.2)'
        }
      ]

      if (this.markLines) {
        const startMarkLineForRedShading = this.markLines.find(x => x.shadeRedAfter)
        const startRedDay = startMarkLineForRedShading ? this.getDay(startMarkLineForRedShading.time) : null

        if (startRedDay !== null) {
          pieces[0].lte = startRedDay
          pieces.push({
            gt: startRedDay,
            lte: this.chartOptions.dataZoom[0].endValue,
            color: 'rgba(183, 28, 28, 0.2)'
          })
        }
      }
      this.chartOptions.visualMap.pieces = pieces
    }
  }
}
</script>
<style lang="css" scoped>
.risk-chart {
  padding-left: 0;
  padding-right: 0;
}

.echarts {
  min-height: 400px;
}

.empty-chart {
  background: repeating-linear-gradient(-55deg, #eee, #eee 10px, #fff 10px, #fff 20px);
  min-height: 75px;
}
</style>
